import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { sendNotification } from '../notifications';
import { supabase } from '../supabaseClient';
import { useAuth } from './AuthContext';
import { DEFAULT_ROLE_PERMS, DEFAULT_SETTINGS } from '../lib/constants';
import { mapAsset, mapFacility, mapDelivery, mapRequest } from '../lib/helpers';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const { profile } = useAuth();

  // ── Core data state ────────────────────────────────────
  const [assets,      setAssets]      = useState([]);
  const [facilities,  setFacilities]  = useState([]);
  const [antennas,    setAntennas]    = useState([]);
  const [requests,    setRequests]    = useState([]);
  const [deliveries,  setDeliveries]  = useState([]);
  const [checkouts,   setCheckouts]   = useState([]);
  const [movements,   setMovements]   = useState([]);
  const [users,       setUsers]       = useState([]);
  const [floorMaps,   setFloorMaps]   = useState({});
  const [settings,    setSettings]    = useState(DEFAULT_SETTINGS);
  const [rolePerms,   setRolePerms]   = useState(DEFAULT_ROLE_PERMS);

  // ── UI state ───────────────────────────────────────────
  const [loading,  setLoading]  = useState(true);
  const [brandLogo,    setBrandLogo]    = useState(localStorage.getItem('rfid_wms_brand_logo') || '');
  const [brandFavicon, setBrandFavicon] = useState('');
  const [toast,    setToast]    = useState(null);

  // ── Toast helper ───────────────────────────────────────
  const showToast = useCallback((msg, type = 'ok') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  }, []);

  // ── Permissions for current user ───────────────────────
  const perms = profile?.role ? (rolePerms[profile.role] || DEFAULT_ROLE_PERMS.Viewer) : DEFAULT_ROLE_PERMS.Viewer;

  // ── Load all data on mount ─────────────────────────────
  useEffect(() => {
    if (!profile) return;
    loadAll();
    subscribeToMovements();
  }, [profile]);

  // Wrap any loader so it never hangs — times out after 8s
  const safe = (fn, name) => Promise.race([
    fn().catch(e => console.warn(`${name} error:`, e?.message || e)),
    new Promise(resolve => setTimeout(() => { console.warn(`${name} timed out`); resolve(); }, 8000)),
  ]);

  const applyFavicon = (url) => {
    if (!url) return;
    let link = document.querySelector("link[rel*='icon']");
    if (!link) { link = document.createElement('link'); link.rel = 'shortcut icon'; document.head.appendChild(link); }
    link.href = url;
  };

  const loadBranding = async () => {
    try {
      const { data } = await supabase
        .from('platform_settings')
        .select('key, value')
        .in('key', ['brand_logo', 'brand_favicon']);
      if (data) {
        const logo    = data.find(r => r.key === 'brand_logo');
        const favicon = data.find(r => r.key === 'brand_favicon');
        const logoVal = logo?.value || '';
        setBrandLogo(logoVal);
        localStorage.setItem('rfid_wms_brand_logo', logoVal);
        if (favicon?.value) { setBrandFavicon(favicon.value); applyFavicon(favicon.value); }
      }
    } catch(e) {}
  };

  // Realtime — reflect branding changes instantly across all open tabs
  useEffect(() => {
    const channel = supabase.channel('branding-changes')
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'platform_settings',
        filter: 'key=in.(brand_logo,brand_favicon)'
      }, (payload) => {
        if (payload.new.key === 'brand_logo') {
          setBrandLogo(payload.new.value || '');
          localStorage.setItem('rfid_wms_brand_logo', payload.new.value || '');
        }
        if (payload.new.key === 'brand_favicon') {
          setBrandFavicon(payload.new.value || '');
          applyFavicon(payload.new.value);
        }
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, []);

  // Check overdue checkouts and low stock after data loads
  const checkNotifications = (allCheckouts, allAssets, cfg) => {
    if (!cfg?.emailNotifications) return;

    // Overdue checkouts
    if (cfg.notifyOverdue) {
      const now = new Date();
      allCheckouts.filter(c => c.status === 'Out' && c.dueDate && new Date(c.dueDate) < now)
        .forEach(c => {
          const days = Math.ceil((now - new Date(c.dueDate)) / 86400000);
          sendNotification('checkout_overdue', {
            assetName:    c.assetName,
            checkedOutTo: c.checkedOutTo,
            dueDate:      c.dueDate,
            daysOverdue:  days,
            facility:     c.facilityName,
          }, cfg);
        });
    }

    // Maintenance due / overdue
    if (cfg.notifyMaintenance) {
      const today = new Date();
      const soon = new Date(today); soon.setDate(soon.getDate() + 30);
      allAssets
        .filter(a => a.next_maintenance_date && a.status !== 'Decommissioned')
        .forEach(a => {
          const next = new Date(a.next_maintenance_date);
          if (next <= soon) {
            const days = Math.ceil((next - today) / 86400000);
            sendNotification('maintenance', {
              assetName: a.name,
              epc:       a.epc,
              facility:  a.currentFacilityName,
              byUser:    days < 0 ? `OVERDUE by ${Math.abs(days)} days` : `Due in ${days} days`,
            }, cfg);
          }
        });
    }

    // Low stock
    if (cfg.lowStockAlerts) {
      const available = allAssets.filter(a => a.status === 'Available').length;
      const threshold = cfg.lowStockThreshold || 10;
      if (available < threshold) {
        sendNotification('low_stock', {
          availableCount: available,
          threshold,
          facilityName:   'All Facilities',
        }, cfg);
      }
    }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      await Promise.all([
        safe(loadFacilities,  'facilities'),
        safe(loadAntennas,    'antennas'),
        safe(loadAssets,      'assets'),
        safe(loadRequests,    'requests'),
        safe(loadDeliveries,  'deliveries'),
        safe(loadCheckouts,   'checkouts'),
        safe(loadMovements,   'movements'),
        safe(loadUsers,       'users'),
        safe(loadSettings,    'settings'),
        safe(loadFloorMaps,   'floorMaps'),
      ]);
    } finally {
      setLoading(false);
    }
  };

  // ── Data loaders ───────────────────────────────────────
  const loadFacilities = async () => {
    const { data } = await supabase.from('facilities').select('*').order('name');
    if (data) setFacilities(data.map(mapFacility));
  };

  const loadAntennas = async () => {
    const { data } = await supabase.from('antennas').select('*').order('id');
    if (data) setAntennas(data);
  };

  const loadAssets = async () => {
    const { data } = await supabase.from('assets').select('*').order('created_at', { ascending: false });
    if (data) setAssets(data.map(mapAsset));
  };

  const loadRequests = async () => {
    const { data } = await supabase.from('requests').select('*').order('created_at', { ascending: false });
    if (data) setRequests(data.map(mapRequest));
  };

  const loadDeliveries = async () => {
    const { data } = await supabase.from('deliveries').select('*').order('created_at', { ascending: false });
    if (data) setDeliveries(data.map(mapDelivery));
  };

  const loadCheckouts = async () => {
    const { data } = await supabase.from('checkouts').select('*').order('checked_out_at', { ascending: false });
    if (data) setCheckouts(data);
  };

  const loadMovements = async () => {
    const { data } = await supabase.from('asset_movements').select('*').order('detected_at', { ascending: false }).limit(200);
    if (data) setMovements(data);
  };

  const loadUsers = async () => {
    if (!profile?.org_id) return;
    const { data } = await supabase.from('profiles').select('*').eq('org_id', profile.org_id).order('name');
    if (data) setUsers(data);
  };

  const loadSettings = async () => {
    try {
      if (!profile?.org_id) return;
      const { data } = await supabase.from('system_settings').select('settings').eq('org_id', profile.org_id).maybeSingle();
      if (data?.settings && Object.keys(data.settings).length > 0) {
        setSettings(prev => ({ ...prev, ...data.settings }));
        if (data.settings.systemName) localStorage.setItem('rfid_wms_system_name', data.settings.systemName);
      }

      // Load branding
      loadBranding();
    } catch (err) {
      console.warn('loadSettings error:', err.message);
    }
  };

  const loadFloorMaps = async () => {
    if (!profile?.org_id) return;
    const { data } = await supabase.from('floor_maps').select('*').eq('org_id', profile.org_id);
    if (data) {
      const maps = {};
      data.forEach(row => { maps[row.facility_id] = row.zones; });   // column is `zones`, not `map_data`
      setFloorMaps(maps);
    }
  };

  // ── Real-time RFID movements (with polling fallback if WS fails) ────────
  const subscribeToMovements = () => {
    let pollTimer = null;
    let usingPoll = false;

    const startPolling = () => {
      if (usingPoll) return;
      usingPoll = true;
      console.info('[Realtime] WebSocket unavailable — falling back to 10s polling');
      pollTimer = setInterval(loadMovements, 10000);
    };
    const stopPolling = () => {
      if (!usingPoll) return;
      usingPoll = false;
      clearInterval(pollTimer);
      pollTimer = null;
    };

    const channel = supabase
      .channel('movements-live')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'asset_movements' }, (payload) => {
        setMovements(prev => [payload.new, ...prev.slice(0, 199)]);
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'movements' }, (payload) => {
        setMovements(prev => [payload.new, ...prev.slice(0, 199)]);
      })
      .subscribe((status) => {
        // status: 'SUBSCRIBED' | 'CLOSED' | 'CHANNEL_ERROR' | 'TIMED_OUT'
        if (status === 'SUBSCRIBED') {
          stopPolling();
          window.__rfidRealtimeStatus = 'online';
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          window.__rfidRealtimeStatus = 'polling';
          startPolling();
        }
      });

    // Safety: if no status callback fires within 5s (some self-hosted setups), start polling anyway
    const safety = setTimeout(() => {
      if (window.__rfidRealtimeStatus !== 'online') startPolling();
    }, 5000);

    return () => {
      clearTimeout(safety);
      stopPolling();
      supabase.removeChannel(channel);
    };
  };

  // ── Save settings to Supabase ──────────────────────────
  const saveSettings = async (fullSettings) => {
    setSettings(fullSettings);
    if (fullSettings.systemName) localStorage.setItem('rfid_wms_system_name', fullSettings.systemName);
    const { error } = await supabase
      .from('system_settings')
      .upsert({ settings: fullSettings, org_id: profile?.org_id }, { onConflict: 'org_id' });
    if (error) throw new Error(error.message || JSON.stringify(error));
    showToast('✓ Settings saved');
  };

  // ── Save floor map ─────────────────────────────────────
  // Stored in floor_maps.zones (jsonb). org_id is NOT NULL + RLS-scoped, so it must be set.
  // No unique constraint on facility_id → upsert can't target it; do update-or-insert manually.
  const saveFloorMap = async (facilityId, mapData) => {
    if (!profile?.org_id) { showToast('❌ No organization', 'error'); return; }
    setFloorMaps(prev => ({ ...prev, [facilityId]: mapData }));

    const { data: existing } = await supabase
      .from('floor_maps')
      .select('id')
      .eq('org_id', profile.org_id)
      .eq('facility_id', facilityId)
      .maybeSingle();

    let error;
    if (existing) {
      ({ error } = await supabase.from('floor_maps').update({ zones: mapData }).eq('id', existing.id));
    } else {
      ({ error } = await supabase.from('floor_maps').insert({ org_id: profile.org_id, facility_id: facilityId, zones: mapData }));
    }

    if (error) { showToast('❌ ' + error.message, 'error'); return; }
    showToast('✓ Floor map saved');
  };

  const value = {
    // Data
    assets, setAssets,
    facilities, setFacilities,
    antennas, setAntennas,
    requests, setRequests,
    deliveries, setDeliveries,
    checkouts, setCheckouts,
    movements, setMovements,
    users, setUsers,
    floorMaps, setFloorMaps,
    settings, setSettings, saveSettings,
    rolePerms, setRolePerms,
    // UI
    loading,
    toast, showToast,
    perms,
    brandLogo, brandFavicon,
    profile,
    // Actions
    reload: loadAll,
    saveFloorMap,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside <AppProvider>');
  return ctx;
};
